import { useQueries } from "@tanstack/react-query";
import { terreiroRepository } from "../repositories/terreiroRepository";
import { terreiroInventoryListQueryKey } from "./useTerreiroInventoryListQuery";

/**
 * Query keys for terreiro inventory items, mediuns, and activity.
 */
export const createEventQuery = {
  inventoryItems: (terreiroId: string | undefined) =>
    terreiroInventoryListQueryKey(terreiroId ?? ""),
  terreiroMediuns: (terreiroId: string | undefined) =>
    ["terreiro", terreiroId, "mediuns"] as const,
  terreiroActivity: (terreiroId: string | undefined) =>
    ["terreiro", terreiroId, "activities"] as const,
  terreiroAddress: (terreiroId: string | undefined) =>
    ["terreiro", terreiroId, "address"] as const,
};

/**
 * Fetches inventory items, mediuns, and MODEL activities for a given terreiro in parallel.
 * MODEL activities are date-independent templates that will be cloned into real instances
 * when the event is created.
 * @param terreiroId The terreiro ID to fetch data for.
 * @param _date Deprecated — kept for backward compatibility but no longer used for activities.
 */
export function useCreateEventQueries(
  terreiroId?: string,
  _date?: string,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && !!terreiroId;
  const queries = useQueries({
    queries: [
      {
        queryKey: createEventQuery.inventoryItems(terreiroId),
        queryFn: () =>
          terreiroId
            ? terreiroRepository.getTerreiroInventoryItems(terreiroId)
            : Promise.resolve([]),
        enabled,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
      },
      {
        queryKey: createEventQuery.terreiroMediuns(terreiroId),
        queryFn: () =>
          terreiroId
            ? terreiroRepository.getTerreiroMediuns(terreiroId, {
                includeSelf: true,
              })
            : Promise.resolve([]),
        enabled,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
      },
      {
        // Fetch MODEL activities (templates) – date-independent
        queryKey: createEventQuery.terreiroActivity(terreiroId),
        queryFn: () =>
          terreiroId
            ? terreiroRepository.getTerreiroActivities(terreiroId, "MODEL")
            : Promise.resolve(null),
        enabled,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
      },
      {
        queryKey: createEventQuery.terreiroAddress(terreiroId),
        queryFn: () =>
          terreiroId
            ? terreiroRepository.getTerreiroAddress(terreiroId)
            : Promise.resolve(null),
        enabled,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
      },
    ],
  });

  const [itemsQuery, mediunsQuery, activityQuery, terreiroAddressQuery] =
    queries;

  // The activities endpoint returns { activities: [...], statusCounts: {...} }
  // when includeCounts=true (default). Extract the array for consumers.
  const rawActivities = activityQuery.data;
  const activitiesList = Array.isArray(rawActivities)
    ? rawActivities
    : rawActivities?.activities || [];

  return {
    items: itemsQuery.data || [],
    mediuns: mediunsQuery.data || [],
    activities: activitiesList,
    addresses: terreiroAddressQuery.data || null,
    isItemsLoading: itemsQuery.isLoading,
    isMediunsLoading: mediunsQuery.isLoading,
    isActivitiesLoading: activityQuery.isLoading,
    isAddressesLoading: terreiroAddressQuery.isLoading,
    isAnyLoading: queries.some((q) => q.isLoading),
    itemsError: itemsQuery.error,
    mediunsError: mediunsQuery.error,
    activitiesError: activityQuery.error,
    addressesError: terreiroAddressQuery.error,
    hasAnyError: queries.some((q) => q.error),
    refetchAll: () => queries.forEach((q) => q.refetch()),
    refetchItems: itemsQuery.refetch,
    refetchMediuns: mediunsQuery.refetch,
    refetchActivities: activityQuery.refetch,
    refetchAddresses: terreiroAddressQuery.refetch,
    isAllSuccess: queries.every((q) => q.isSuccess && q.data !== undefined),
    isSuccess: queries.every((q) => q.isSuccess),
    isError: queries.some((q) => q.isError),
  };
}
